import { forkJoin, iif as observableIif, Observable, of } from 'rxjs';
import { Component, Input, OnChanges, OnInit, QueryList, SimpleChanges, TemplateRef, ViewChildren } from '@angular/core';
import { LocalLoad, LocalLoadStatus } from '../../shared/local-load.model';
import { LocalStop } from '../../shared/local-stop.model';
import { LocalLoadService } from '../../shared/local-load.service';
import { LoaderService } from '../../../shared/services/loader.service';
import { StopDetailsComponent } from '../stop-details/stop-details.component';
import { LoadNoteTypeService } from '../../shared/load-note-type.service';
import { LoadNoteType } from '../../shared/load-note-type.model';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { Carrier } from '../../../user-dashboard/shared/carrier.model';
import { finalize } from 'rxjs/operators';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { ModalService } from '../../../shared/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';


@Component({
  selector: 'app-load-details',
  templateUrl: './load-details.component.html',
  styleUrls: ['./load-details.component.scss']
})
export class LoadDetailsComponent implements OnInit, OnChanges {

  @Input()
  load: LocalLoad;

  @Input()
  stop: LocalStop;

  @Input()
  carrier: Carrier;

  @ViewChildren('stopElements')
  stopElements: QueryList<StopDetailsComponent>;

  noteTypes: LoadNoteType[] = [];

  canResetStatus = false;

  constructor(
    private authService: AuthenticationService,
    private localLoadService: LocalLoadService,
    private loadNoteTypeService: LoadNoteTypeService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private modal: ModalService,
    private translate: TranslateService,
    private toastr: ToastrService,
  ) {
    if (!this.authService.canManageCarriers()) {
      this.getLoadNoteTypes();
    }
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.load && changes.load.currentValue) {
      this.getLoadDetails();
    }

    if (changes.load && changes.load.previousValue) {
      const previousLoad: LocalLoad = changes.load.previousValue;

      if (previousLoad.expanded) {
        previousLoad.expanded = false;
      }
    }

    if (changes.stop && changes.stop.currentValue) {
      const stopComponent = this.stopElements.find(sc => sc.stop === changes.stop.currentValue);

      if (stopComponent) {
        stopComponent.scrollTo();
      }
    }

    if (changes.carrier && changes.carrier.currentValue) {
      this.getLoadNoteTypes();
    }
  }

  getLoadNoteTypes() {
    this.loader.startLoad();

    let getNoteTypesObservable: Observable<LoadNoteType[]>;

    if (this.authService.canManageCarriers()) {
      getNoteTypesObservable = this.loadNoteTypeService.getWritablesForCarrier(this.carrier);
    } else {
      getNoteTypesObservable = this.loadNoteTypeService.getWritablesForSelfCarrier();
    }

    getNoteTypesObservable
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(noteTypes => {
        this.noteTypes = noteTypes;
      }, e => this.errorHandler.handleHttpError(e, 'local-loads.search.details.errors.get-load-note-types', false));
  }

  openResetStatus(content: TemplateRef<any>) {
    this.modal.open({
      title: this.translate.instant('local-loads.search.modals.reset-status.title'),
      content,
      validateButton: {
        text: this.translate.instant('local-loads.search.details.reset-status'),
        type: "warning",
        icon: 'fa fa-history',
      },
    }).subscribe(() => this.resetStatus());

  }

  resetStatus() {
    this.loader.startLoad();

    this.localLoadService.resetLocalLoadStatus(this.load.id)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.toastr.success(this.translate.instant('local-loads.search.details.successes.reset-status'));
        this.load.status   = LocalLoadStatus.PLANNED;
        this.canResetStatus = false;
        this.getLoadDetails(false);
      }, e => this.errorHandler.handleHttpError(e, 'local-loads.search.details.errors.reset-status', false));
  }

  getLoadDetails(refreshAlsoLoad = false) {
    const loadObservable          = observableIif(
      () => refreshAlsoLoad,
      this.localLoadService.getById(this.load.id),
      of(this.load)
    );
    const eventsObservable        = this.localLoadService.getEvents(this.load);
    const communicationObservable = this.localLoadService.getCommunication(this.load);
    const documentsObservable     = this.localLoadService.getDocuments(this.load);

    this.loader.startLoad();

    forkJoin([loadObservable, eventsObservable, communicationObservable, documentsObservable])
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(([load, events, communication, documents]) => {
        this.load               = load;
        this.load.events        = events;
        this.load.communication = communication;
        this.load.documents     = documents;

        this.load.updateShipmentsPod();
        this.load.mergePicturesIntoNotes();

        this.canResetStatus = this.load.status !== LocalLoadStatus.PLANNED;
      }, e => this.errorHandler.handleHttpError(e, 'local-loads.search.details.errors.reset-status', false));
  }
}
